import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Heart, Shield, AlertTriangle, ExternalLink, Phone, Mail } from 'lucide-react';

const ResponsibleGaming = () => {
  return (
    <div className="min-h-screen bg-beige-50">
      <Header />
      <main className="pt-20">
        <article className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="glass-strong rounded-3xl p-8 md:p-12 mb-8">
              <div className="flex items-center space-x-3 mb-6">
                <Heart className="w-10 h-10 text-brown-700" />
                <h1 className="text-4xl md:text-5xl font-bold text-brown-900">Responsible Gaming</h1>
              </div>
              <p className="text-xl text-brown-700 leading-relaxed">
                Sterling Spins is committed to promoting safe, responsible gambling. This page provides 
                information, tools, and resources to help you maintain healthy gaming habits.
              </p>
            </div>

            <div className="space-y-8">
              <section className="glass rounded-2xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <Heart className="w-8 h-8 text-brown-700" />
                  <h2 className="text-3xl font-bold text-brown-900">Our Commitment to Responsible Gambling</h2>
                </div>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    At Sterling Spins, we believe that gambling should be an entertaining and enjoyable 
                    activity that enhances your leisure time, not a source of financial problems or 
                    personal distress. We are committed to promoting responsible gambling practices and 
                    providing resources to help you gamble safely and within your means.
                  </p>
                  <p>
                    As an independent review and comparison platform, we do not operate online casinos 
                    directly. However, we take our responsibility seriously to ensure that our content 
                    promotes safe gambling practices and directs players toward licensed, reputable 
                    operators that prioritize player protection and responsible gambling measures.
                  </p>
                  <p>
                    We encourage all visitors to our website to gamble responsibly, set limits before 
                    playing, and seek help if gambling becomes problematic. This page provides comprehensive 
                    information about responsible gambling tools, warning signs, and support resources 
                    available to UK players.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <AlertTriangle className="w-8 h-8 text-brown-700" />
                  <h2 className="text-3xl font-bold text-brown-900">Recognizing Problem Gambling</h2>
                </div>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    Problem gambling can affect anyone, regardless of age, background, or financial 
                    situation. Recognizing warning signs early is crucial for preventing gambling from 
                    becoming harmful. Problem gambling is not defined by how much you spend or how often 
                    you play—it's determined by the impact gambling has on your life, relationships, and 
                    overall well-being.
                  </p>
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Warning Signs to Watch For:</h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Spending more money or time gambling than you can afford</li>
                      <li>Chasing losses by gambling more to win back money</li>
                      <li>Lying to family or friends about gambling activities</li>
                      <li>Feeling anxious, depressed, or irritable when not gambling</li>
                      <li>Neglecting work, school, or family responsibilities due to gambling</li>
                      <li>Borrowing money or selling possessions to fund gambling</li>
                      <li>Feeling unable to stop or reduce gambling despite wanting to</li>
                      <li>Gambling to escape problems or relieve feelings of stress</li>
                    </ul>
                  </div>
                  <p>
                    If you recognize these signs in yourself or someone you know, it's important to seek 
                    help immediately. Problem gambling is a treatable condition, and early intervention 
                    significantly improves outcomes. Support is available through multiple channels, and 
                    you don't have to face gambling problems alone.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <Shield className="w-8 h-8 text-brown-700" />
                  <h2 className="text-3xl font-bold text-brown-900">Responsible Gambling Tools</h2>
                </div>
                <div className="space-y-6 text-brown-700 leading-relaxed">
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Deposit Limits</h3>
                    <p>
                      Deposit limits allow you to cap how much you can deposit over daily, weekly, or 
                      monthly periods. These limits are set before you start gambling and cannot be 
                      increased immediately—most operators require a 24-48 hour cooling-off period before 
                      raising limits. This delay prevents impulsive decisions during emotional moments and 
                      helps you stick to budgets you set when thinking clearly.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Loss Limits</h3>
                    <p>
                      Loss limits cap total losses over specified periods, providing additional protection 
                      beyond deposit controls. When losses reach limit thresholds, you must wait until 
                      limits reset before continuing play, creating automatic breaks that prevent chasing 
                      losses with increased betting. Combined with deposit limits, loss limits create 
                      comprehensive spending controls.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Session Time Limits</h3>
                    <p>
                      Session time limits automatically log you out after predetermined periods, forcing 
                      breaks that allow time to reassess your gaming activity. These limits prevent 
                      extended sessions where decision-making quality degrades due to fatigue or emotional 
                      states. Most operators allow you to customize time limits to match your preferences.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Reality Checks</h3>
                    <p>
                      Reality check notifications appear at regular intervals during gameplay, displaying 
                      how long you've been playing, total deposits made, and current account balance. 
                      These reminders break the immersive flow that can cause you to lose track of time 
                      and spending, providing moments to consciously decide whether to continue playing 
                      or take a break.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Self-Exclusion</h3>
                    <p>
                      Self-exclusion allows you to block access to your casino account for predetermined 
                      periods ranging from 24 hours to permanent exclusion. Once activated, casinos cannot 
                      process deposits, allow gameplay, or send promotional communications until the 
                      exclusion period expires. GamStop is the UK's national self-exclusion service, 
                      allowing you to exclude from all UK-licensed operators simultaneously.
                    </p>
                  </div>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Tips for Responsible Gambling</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Set a Budget</h3>
                    <p>
                      Before you start gambling, decide how much money you can afford to lose and stick to 
                      that amount. Never gamble with money you need for essential expenses like rent, bills, 
                      or groceries. Treat gambling as paid entertainment with a cost, similar to cinema 
                      tickets or dining out, rather than an investment or income opportunity.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Set Time Limits</h3>
                    <p>
                      Decide how long you want to gamble before starting a session, and stick to that time 
                      limit regardless of whether you're winning or losing. Use alarms or timers to remind 
                      yourself when your session time is up. Taking regular breaks helps maintain 
                      perspective and prevents extended sessions that can lead to poor decision-making.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Never Chase Losses</h3>
                    <p>
                      Losing is part of gambling, and trying to win back losses by gambling more typically 
                      leads to bigger losses. Accept losses as the cost of entertainment, and never increase 
                      bets or gamble longer to recover losses. If you find yourself chasing losses, it's 
                      time to stop and take a break.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Don't Gamble When Emotional</h3>
                    <p>
                      Avoid gambling when you're feeling stressed, anxious, depressed, or upset. Emotional 
                      states impair decision-making and increase the risk of problematic gambling behavior. 
                      Wait until you're thinking clearly and feeling balanced before deciding to gamble.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-brown-900 mb-3">Balance Gambling with Other Activities</h3>
                    <p>
                      Maintain diverse interests, relationships, and activities beyond gambling. When 
                      gambling becomes your main hobby or social activity, it's time to reassess and 
                      reduce involvement to healthier levels. Healthy gambling fits into a balanced life 
                      rather than dominating it.
                    </p>
                  </div>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Support Resources</h2>
                <div className="space-y-6 text-brown-700 leading-relaxed">
                  <div className="bg-brown-100 rounded-lg p-6">
                    <div className="flex items-start space-x-4 mb-4">
                      <Phone className="w-6 h-6 text-brown-700 mt-1" />
                      <div>
                        <h3 className="text-xl font-semibold text-brown-900 mb-2">BeGambleAware</h3>
                        <p className="mb-3">
                          BeGambleAware provides free, confidential support 24/7 through their helpline, 
                          online chat, and email services. Trained counselors understand gambling problems 
                          without judgment, offering practical advice and referrals to treatment programs.
                        </p>
                        <a 
                          href="https://www.begambleaware.org" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 text-brown-800 hover:text-brown-900 font-semibold"
                        >
                          <span>Visit BeGambleAware.org</span>
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <p className="mt-2">Helpline: 0808 8020 133</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-brown-100 rounded-lg p-6">
                    <div className="flex items-start space-x-4 mb-4">
                      <Heart className="w-6 h-6 text-brown-700 mt-1" />
                      <div>
                        <h3 className="text-xl font-semibold text-brown-900 mb-2">GamCare</h3>
                        <p className="mb-3">
                          GamCare offers similar support services plus online forums where people share 
                          experiences and strategies for maintaining control. Their treatment programs 
                          include individual counseling, group therapy, and online support groups.
                        </p>
                        <a 
                          href="https://www.gamcare.org.uk" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 text-brown-800 hover:text-brown-900 font-semibold"
                        >
                          <span>Visit GamCare.org.uk</span>
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <p className="mt-2">Helpline: 0808 8020 133</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-brown-100 rounded-lg p-6">
                    <div className="flex items-start space-x-4 mb-4">
                      <Shield className="w-6 h-6 text-brown-700 mt-1" />
                      <div>
                        <h3 className="text-xl font-semibold text-brown-900 mb-2">GamStop</h3>
                        <p className="mb-3">
                          GamStop is the UK's national self-exclusion service, allowing you to exclude from 
                          all UK-licensed operators simultaneously. Exclusions range from six months to five 
                          years, providing comprehensive protection when you need time away from gambling.
                        </p>
                        <a 
                          href="https://www.gamstop.co.uk" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 text-brown-800 hover:text-brown-900 font-semibold"
                        >
                          <span>Visit GamStop.co.uk</span>
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="bg-brown-100 rounded-lg p-6">
                    <div className="flex items-start space-x-4 mb-4">
                      <Mail className="w-6 h-6 text-brown-700 mt-1" />
                      <div>
                        <h3 className="text-xl font-semibold text-brown-900 mb-2">Gambling Therapy</h3>
                        <p className="mb-3">
                          Gambling Therapy provides global support services for anyone affected by problem 
                          gambling. Their online support service is available in multiple languages, offering 
                          advice and support through forums, live chat, and email.
                        </p>
                        <a 
                          href="https://www.gamblingtherapy.org" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 text-brown-800 hover:text-brown-900 font-semibold"
                        >
                          <span>Visit GamblingTherapy.org</span>
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">For Family and Friends</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    Problem gambling doesn't only affect the person gambling—it impacts family members, 
                    friends, and loved ones as well. If someone you care about is struggling with problem 
                    gambling, support resources are available to help you understand the situation and 
                    provide appropriate assistance.
                  </p>
                  <p>
                    BeGambleAware and GamCare offer support services specifically for family members and 
                    friends affected by someone else's gambling problem. These services provide guidance 
                    on how to support your loved one while protecting yourself from financial harm and 
                    emotional distress.
                  </p>
                  <p>
                    Remember that you cannot force someone to stop gambling or seek help—they must make 
                    that decision themselves. However, you can encourage them to access support resources, 
                    set boundaries to protect yourself, and seek support for your own well-being. Taking 
                    care of yourself is essential when supporting someone with a gambling problem.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Underage Gambling</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    Online gambling is strictly prohibited for individuals under 18 years of age in the 
                    United Kingdom. All UK-licensed operators are required to verify age and identity before 
                    allowing deposits or gameplay. Sterling Spins does not provide services to minors, and 
                    our website content is intended for adults only.
                  </p>
                  <p>
                    If you are a parent or guardian concerned about a child's access to gambling websites, 
                    parental control software and filtering services can help restrict access. Educational 
                    conversations about the risks of gambling can also help children understand why 
                    gambling is restricted to adults.
                  </p>
                  <p>
                    If you suspect a child has accessed gambling services, contact the relevant casino 
                    operator immediately. All UK-licensed operators take underage gambling seriously and 
                    will take appropriate action to prevent access and refund any deposits made by minors.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Our Role in Promoting Responsible Gambling</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    As a review and comparison platform, Sterling Spins plays an important role in 
                    promoting responsible gambling. We only feature casinos that hold valid UK Gambling 
                    Commission licenses, ensuring that players access operators that are required to 
                    implement comprehensive responsible gambling tools and player protection measures.
                  </p>
                  <p>
                    Our reviews highlight responsible gambling features available at each casino, helping 
                    you identify operators that prioritize player protection. We provide information about 
                    deposit limits, self-exclusion options, and support resources, enabling you to make 
                    informed decisions about where to play based on safety considerations as well as game 
                    selection and bonus offers.
                  </p>
                  <p>
                    We regularly update our content to reflect changes in responsible gambling tools and 
                    support resources, ensuring that information remains current and helpful. If you have 
                    concerns about any casino operator or need guidance on responsible gambling resources, 
                    please contact us at info@sterling-spins.com.
                  </p>
                </div>
              </section>

              <section className="glass rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-brown-900 mb-6">Getting Help</h2>
                <div className="space-y-4 text-brown-700 leading-relaxed">
                  <p>
                    If you're struggling with problem gambling, remember that help is available and 
                    recovery is possible. Seeking support is a sign of strength, not weakness, and early 
                    intervention significantly improves outcomes. You don't have to face gambling problems 
                    alone—multiple support services are available to help you regain control.
                  </p>
                  <p>
                    Contact any of the support organizations listed above, or speak with your GP who can 
                    refer you to local treatment services. Many people successfully overcome problem 
                    gambling with appropriate support and treatment, and taking the first step of seeking 
                    help is often the most important action you can take.
                  </p>
                  <div className="bg-brown-100 rounded-lg p-6 mt-6">
                    <p className="font-semibold text-brown-900 mb-4">Need Immediate Support?</p>
                    <p className="text-brown-800 mb-2">
                      <strong>National Gambling Helpline:</strong> 0808 8020 133 (Free, confidential, 24/7)
                    </p>
                    <p className="text-brown-800">
                      <strong>BeGambleAware Live Chat:</strong> Available 24/7 at www.begambleaware.org
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default ResponsibleGaming;

